/**
 * Simple in-memory cache with TTL (time-to-live) support
 * Implements stale-while-revalidate pattern for better UX
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    // In-flight requests, keyed the same way as `cache`. Lets concurrent
    // callers for the same key share one network request instead of each
    // firing their own before the first response lands.
    this.pending = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  /**
   * Get the in-flight request for a key, if there is one
   */
  getPending(key) {
    return this.pending.get(key) || null;
  }

  /**
   * Register an in-flight request for a key
   */
  setPending(key, promise) {
    this.pending.set(key, promise);
  }

  /**
   * Drop the in-flight request for a key (only if it is still the one given,
   * so a late-finishing request can't clear a newer one)
   */
  clearPending(key, promise) {
    if (!promise || this.pending.get(key) === promise) {
      this.pending.delete(key);
    }
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${JSON.stringify(params[key])}`)
      .join('&');
    return `${url}${paramString ? '?' + paramString : ''}`;
  }

  /**
   * Get cached data
   * Returns { data, isStale } where isStale indicates if data needs refresh
   */
  get(key) {
    const cached = this.cache.get(key);

    if (!cached) {
      return { data: null, isStale: false };
    }

    const now = Date.now();
    const age = now - cached.timestamp;

    // Data is fresh (within TTL)
    if (age < cached.ttl) {
      return { data: cached.data, isStale: false };
    }

    // Data is stale but still usable (stale-while-revalidate)
    // Return stale data but indicate it needs refresh
    if (age < cached.ttl * 2) {
      return { data: cached.data, isStale: true };
    }

    // Data is too old, remove it
    this.cache.delete(key);
    return { data: null, isStale: false };
  }

  /**
   * Set cache data with TTL
   */
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Invalidate specific cache key
   *
   * Also drops any in-flight request for that key. A request that started
   * before an invalidating write would otherwise write a pre-write snapshot
   * into the cache when it lands; dropping it here makes the writer that
   * checks `getPending` see it is no longer current and skip the `set`.
   */
  invalidate(key) {
    this.cache.delete(key);
    this.pending.delete(key);
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern) {
    const keys = Array.from(new Set([...this.cache.keys(), ...this.pending.keys()]));
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        this.pending.delete(key);
      }
    });
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.pending.clear();
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const apiCache = new ApiCache();

/**
 * Higher-order function to wrap API calls with caching
 *
 * @param {Function} apiFn - The API function to wrap
 * @param {Object} options - Caching options
 * @param {number} options.ttl - Time to live in milliseconds
 * @param {string} options.key - Custom cache key (optional)
 * @returns {Function} Wrapped API function with caching
 *
 * @example
 * const getCachedUser = withCache(getUser, { ttl: 5 * 60 * 1000 });
 * const userData = await getCachedUser();
 */
export function withCache(apiFn, options = {}) {
  const { ttl, key: customKey } = options;

  /**
   * Fetch for a key, sharing one request between concurrent callers.
   *
   * On a cold load many components ask for the same resource (e.g. the top
   * bar, the bottom tabs and the page body all want the user) before any
   * response has landed, so without this each one opens its own request.
   */
  const fetchDeduped = (cacheKey, args) => {
    const existing = apiCache.getPending(cacheKey);
    if (existing) return existing;

    let request;
    request = apiFn(...args)
      .then(freshData => {
        // Only cache if this request is still the current one — an
        // invalidation while it was in flight means the result is already
        // out of date and must not be written back.
        if (apiCache.getPending(cacheKey) === request) {
          apiCache.set(cacheKey, freshData, ttl);
        }
        return freshData;
      })
      .finally(() => {
        apiCache.clearPending(cacheKey, request);
      });

    apiCache.setPending(cacheKey, request);
    return request;
  };

  return async function(...args) {
    // Generate cache key
    const cacheKey = customKey || apiCache.generateKey(
      apiFn.name,
      args.length > 0 ? { args: args } : {}
    );

    // Check cache first
    const { data: cachedData, isStale } = apiCache.get(cacheKey);

    if (cachedData && !isStale) {
      return cachedData;
    }

    // If data is stale, return it immediately but refresh in background
    if (cachedData && isStale) {

      // Refresh in background (don't await)
      fetchDeduped(cacheKey, args)
        .catch(error => {
          console.error(`[Cache REFRESH FAILED] ${cacheKey}:`, error);
        });

      return cachedData;
    }

    // Cache miss - fetch fresh data
    try {
      return await fetchDeduped(cacheKey, args);
    } catch (error) {
      // On error, still return stale data if available
      if (cachedData) {
        console.warn(`[Cache ERROR] ${cacheKey} - returning stale data due to error:`, error);
        return cachedData;
      }
      throw error;
    }
  };
}

export default apiCache;
